/**
 * What a screen reader is told as calls come and go.
 *
 * The dock's visual state — a pulsing ring, a timer, a tile turning blue — says nothing to
 * somebody who cannot see it, and nothing to a sighted rep whose eyes are on a lead record two
 * screens away. So the transitions that matter are put into words and pushed through a live
 * region: a pure diff of the call list before and after, kept out of React so the sentences can
 * be asserted in a test.
 *
 * `urgent` is the one thing that separates an incoming call from the rest. It is read out over
 * whatever the reader is in the middle of, because the call has a deadline; everything else
 * waits its turn.
 */

export interface AnnouncedCall {
  id: string;
  direction: "inbound" | "outbound";
  state: string;
  muted: boolean;
  /** Who the call is with, as the dock shows it — a name, a grouped number, a room. */
  label: string;
}

export interface Announcement {
  text: string;
  urgent: boolean;
}

export function callAnnouncements(
  previous: readonly AnnouncedCall[],
  next: readonly AnnouncedCall[],
): Announcement[] {
  const out: Announcement[] = [];
  const before = new Map(previous.map((c) => [c.id, c]));
  const after = new Set(next.map((c) => c.id));

  // Endings first: "Call with A ended. Back with B." is the order it happened in.
  for (const c of previous) {
    if (!after.has(c.id)) out.push({ text: `Call with ${c.label} ended`, urgent: false });
  }

  for (const c of next) {
    const was = before.get(c.id);
    if (!was) {
      if (c.direction === "inbound" && c.state === "ringing") {
        out.push({ text: `Incoming call from ${c.label}`, urgent: true });
      } else if (c.state === "active" || c.state === "held") {
        // A call that appears already up was re-attached after a reload.
        out.push({ text: `Connected to ${c.label}`, urgent: false });
      } else if (c.direction === "outbound") {
        out.push({ text: `Calling ${c.label}`, urgent: false });
      }
      continue;
    }
    if (was.state !== c.state) {
      if (c.state === "active" && was.state === "held") {
        out.push({ text: `Back with ${c.label}`, urgent: false });
      } else if (c.state === "active") {
        out.push({ text: `Connected to ${c.label}`, urgent: false });
      } else if (c.state === "held") {
        out.push({ text: `${c.label} on hold`, urgent: false });
      }
    }
    if (was.muted !== c.muted) {
      out.push({ text: c.muted ? "Microphone muted" : "Microphone on", urgent: false });
    }
  }
  return out;
}
